import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ExtractionQuestion } from '@trinity/chat';

type UseTagExtractionQuestionsParams = {
  projectId: number;
  tagId: number;
};

export const useTagExtractionQuestions = ({ projectId, tagId }: UseTagExtractionQuestionsParams) => {
  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();
  const queryKey = ['get:tag-extraction-questions', projectId, tagId] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => apiClient.projects.getTagExtractionQuestions({ projectId, tagId }),
    enabled: tagId > 0,
  });

  const mutation = useMutation({
    mutationFn: (questions: ExtractionQuestion[]) =>
      apiClient.projects.updateTagExtractionQuestions({
        projectId,
        tagId,
        questions: questions.map((question, idx) => ({
          identifier: getQuestionIdentifier(question, query.data?.items ?? [], idx),
          question,
          ordinal: idx,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const items: ExtractionQuestion[] =
    query.data?.items.map((item) => item.question) ?? [];

  return {
    items,
    isLoading: query.isLoading,
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
};

/**
 * Resolves the identifier for a question:
 * - If it matches an existing item by index, reuse its identifier
 * - Otherwise generate a new one via crypto.randomUUID()
 */
function getQuestionIdentifier(
  _question: ExtractionQuestion,
  existingItems: Array<{ identifier: string; question: ExtractionQuestion }>,
  index: number
): string {
  if (index < existingItems.length) {
    return existingItems[index].identifier;
  }
  return crypto.randomUUID();
}
